import { ExternalLink, Github, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import type { Project } from "@portfolio/types";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/lib/api";

export interface ProjectCardProps {
  project: Project;
  variant?: "grid" | "list";
  className?: string;
}

export function ProjectCard({ project, variant = "grid", className }: ProjectCardProps) {
  return (
    <motion.article
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
      className={cn(
        "group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card/70 backdrop-blur",
        className
      )}
    >
      <Link
        to={`/projects/${project.slug}`}
        className="relative block aspect-[16/10] overflow-hidden bg-gradient-to-br from-primary/20 via-amber-200/10 to-background"
      >
        {project.coverImageUrl ? (
          <img
            src={project.coverImageUrl}
            alt={project.title}
            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
            loading="lazy"
          />
        ) : (
          <PlaceholderArt title={project.title} />
        )}
        {project.isFeatured && (
          <div className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-1 text-[10px] uppercase tracking-wider text-primary">
            <Sparkles className="h-3 w-3" /> Featured
          </div>
        )}
      </Link>

      <div className={cn("flex flex-1 flex-col gap-3 p-5", variant === "list" && "sm:flex-row")}>
        <div className="flex-1">
          <h3 className="font-serif text-xl text-foreground transition group-hover:text-primary">
            <Link to={`/projects/${project.slug}`}>{project.title}</Link>
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">{project.description}</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {project.tags.slice(0, 6).map((tag) => (
            <span
              key={tag}
              className="rounded-full border border-border bg-background/60 px-2 py-0.5 text-[11px] text-muted-foreground"
            >
              {tag}
            </span>
          ))}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
          {project.liveUrl && (
            <a
              href={project.liveUrl}
              target="_blank"
              rel="noreferrer noopener"
              onClick={() =>
                trackEvent({
                  eventType: "project_click",
                  entityId: project.id,
                  path: project.liveUrl,
                })
              }
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Live
            </a>
          )}
          {project.githubUrl && (
            <a
              href={project.githubUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
            >
              <Github className="h-3.5 w-3.5" /> Source
            </a>
          )}
        </div>
      </div>
    </motion.article>
  );
}

function PlaceholderArt({ title }: { title: string }) {
  // Decorative fallback when no cover image is uploaded.
  const letter = title?.[0]?.toUpperCase() ?? "?";
  return (
    <div className="grid h-full w-full place-items-center">
      <div className="font-serif text-7xl text-primary/40">{letter}</div>
      <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
    </div>
  );
}
